import { useIsMobile } from '../../../../../utils/getIsMobile';
import { IconCalendarWithClock } from '../../../../icon/IconCalendarWithClock';
import { IconLikeAction } from '../../../../icon/IconLikeAction';
import { IconLikeFilled } from '../../../../icon/IconLikeFilled';
import { IconThumbUpAction } from '../../../../icon/IconThumbUpAction';
import { IconThumbUpActionFilled } from '../../../../icon/IconThumbUpActionFilled';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Typography } from '../../../../typography/Typography';
import { isPostStatusValidForSave } from '../../../PostDetailsCard/parts/utils/isPostStatusValidForSave';
import { isPostStatusValidForReaction } from '../../../utils/isPostStatusValidForReaction';
import { ViewsBadge } from '../ViewsBadge/ViewsBadge';
import { useCardActionsFlags } from './hooks/useCardActionsFlags';
import { useCardActionsHandlers } from './hooks/useCardActionsHandlers';
import {
    BookingButton,
    Button,
    CardActionsWrapperWithFavorites,
    RightBadgesWrapper,
    TextWrapperWithFavorites,
    TopRightActionsWrapper,
} from './styles';
import { CardActionsProps } from './types';

export const CardActionsWithFavorites: React.FC<CardActionsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    ...other
}) => {
    const { text, reactionState, saved, publicStats } = post;
    const isMobile = useIsMobile();
    const { withCheckout, hasBookingBadge, hasViewsBadge } = useCardActionsFlags(post);
    const { onLikeClick, onBookmarkClick } = useCardActionsHandlers({
        index,
        onReaction,
        onBookmark,
    });

    const hasRightBottomBadges = hasViewsBadge || hasBookingBadge || withCheckout;
    const canSave = isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner;
    const canReact = isPostStatusValidForReaction(post.status);
    const isDescriptionFullWidth = !canReact && !canSave && !hasRightBottomBadges;

    return (
        <CardActionsWrapperWithFavorites {...other} isMobile={isMobile}>
            <TopRightActionsWrapper>
                {canSave ? (
                    <Button onClick={onBookmarkClick} dataTestId={DataTestId.BOOKMARK}>
                        {saved ? (
                            <IconLikeFilled
                                fontSize={16}
                                color="var(--always-white)"
                                dataTestId={DataTestId.FILLED}
                            />
                        ) : (
                            <IconLikeAction
                                fontSize={16}
                                color="var(--always-white)"
                                dataTestId={DataTestId.EMPTY}
                            />
                        )}
                    </Button>
                ) : null}
                {canReact ? (
                    <Button onClick={onLikeClick} dataTestId={DataTestId.LIKE}>
                        {reactionState === 'like' ? (
                            <IconThumbUpActionFilled
                                fontSize={16}
                                color="var(--always-white)"
                                dataTestId={DataTestId.FILLED}
                            />
                        ) : (
                            <IconThumbUpAction
                                fontSize={16}
                                color="var(--always-white)"
                                dataTestId={DataTestId.EMPTY}
                            />
                        )}
                    </Button>
                ) : null}
            </TopRightActionsWrapper>

            {text ? (
                <TextWrapperWithFavorites
                    hasRightBadges={hasRightBottomBadges}
                    maxWidth={isDescriptionFullWidth ? '100%' : undefined}
                    width={isDescriptionFullWidth ? '100%' : undefined}
                >
                    <Typography
                        variant="caption"
                        color="var(--always-white)"
                        width="100%"
                        lines={3}
                    >
                        {text}
                    </Typography>
                </TextWrapperWithFavorites>
            ) : null}

            {hasViewsBadge || hasBookingBadge ? (
                <RightBadgesWrapper>
                    {hasBookingBadge ? (
                        <BookingButton>
                            <IconCalendarWithClock fontSize={20} color="var(--always-white)" />
                        </BookingButton>
                    ) : null}
                    {hasViewsBadge ? (
                        <ViewsBadge
                            views={publicStats?.shows || 0}
                            background="var(--content-overlay-medium)"
                        />
                    ) : null}
                </RightBadgesWrapper>
            ) : null}
        </CardActionsWrapperWithFavorites>
    );
};
